import React, { useState, useEffect, useRef } from "react";
import {
  ShoppingCart,
  Plus,
  Minus,
  Trash2,
  CreditCard,
  Banknote,
  Search,
  Receipt,
  Printer,
  X,
  Calendar,
} from "lucide-react";
import api from "../../services/api";

const POS = () => {
  const [items, setItems] = useState([]);
  const [cart, setCart] = useState([]);
  const [search, setSearch] = useState("");
  const [customer, setCustomer] = useState({ name: "", phone: "" });
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [loading, setLoading] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [activeTab, setActiveTab] = useState("sale");
  const [stats, setStats] = useState({ todaySales: 0, todayTransactions: 0 });
  const [showReceipt, setShowReceipt] = useState(false);
  const [lastSale, setLastSale] = useState(null);
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split("T")[0];
  });
  const [dateTo, setDateTo] = useState(
    () => new Date().toISOString().split("T")[0],
  );
  const receiptRef = useRef(null);

  useEffect(() => {
    fetchItems();
    fetchStats();
  }, []);

  useEffect(() => {
    fetchTransactions();
  }, [dateFrom, dateTo]);

  const fetchItems = async () => {
    try {
      const res = await api.get("/inventory/items", { params: { limit: 100 } });
      setItems(
        res.data.data.items.filter((i) => parseFloat(i.current_stock) > 0),
      );
    } catch (err) {
      console.error(err);
    }
  };

  const fetchTransactions = async () => {
    try {
      const res = await api.get("/pos/transactions", {
        params: { date_from: dateFrom, date_to: dateTo, limit: 1000 },
      });
      setTransactions(res.data.data.transactions);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await api.get("/pos/dashboard");
      setStats(res.data.data);
    } catch (err) {
      console.error(err);
    }
  };

  const addToCart = (item) => {
    const existing = cart.find((c) => c.item_id === item.id);
    if (existing) {
      if (existing.quantity < parseFloat(item.current_stock)) {
        setCart(
          cart.map((c) =>
            c.item_id === item.id ? { ...c, quantity: c.quantity + 1 } : c,
          ),
        );
      }
    } else {
      setCart([
        ...cart,
        {
          item_id: item.id,
          name: item.name,
          sku: item.sku,
          quantity: 1,
          unit_price: parseFloat(item.selling_price || item.unit_cost),
          max_stock: parseFloat(item.current_stock),
        },
      ]);
    }
  };

  const updateQuantity = (itemId, delta) => {
    setCart(
      cart.map((c) => {
        if (c.item_id === itemId) {
          const newQty = c.quantity + delta;
          if (newQty > 0 && newQty <= c.max_stock)
            return { ...c, quantity: newQty };
        }
        return c;
      }),
    );
  };

  const setQuantity = (itemId, qty) => {
    const numQty = parseInt(qty) || 0;
    setCart(
      cart.map((c) => {
        if (c.item_id === itemId) {
          if (numQty > 0 && numQty <= c.max_stock)
            return { ...c, quantity: numQty };
          if (numQty > c.max_stock) return { ...c, quantity: c.max_stock };
        }
        return c;
      }),
    );
  };

  const removeFromCart = (itemId) =>
    setCart(cart.filter((c) => c.item_id !== itemId));

  const subtotal = cart.reduce((sum, c) => sum + c.quantity * c.unit_price, 0);
  const total = subtotal;

  const completeSale = async () => {
    if (!cart.length) return alert("Cart is empty");
    setLoading(true);
    try {
      const res = await api.post("/pos/transactions", {
        items: cart.map((c) => ({
          item_id: c.item_id,
          quantity: c.quantity,
          unit_price: c.unit_price,
        })),
        customer_name: customer.name || null,
        customer_phone: customer.phone || null,
        payment_method: paymentMethod,
      });

      // Store sale data for receipt
      setLastSale({
        transaction_number:
          res.data.data?.transaction?.transaction_number || `TXN-${Date.now()}`,
        date: new Date().toLocaleString(),
        items: [...cart],
        subtotal,
        total,
        customer: { ...customer },
        payment_method: paymentMethod,
      });
      setShowReceipt(true);

      setCart([]);
      setCustomer({ name: "", phone: "" });
      fetchItems();
      fetchTransactions();
      fetchStats();
    } catch (err) {
      alert(err.response?.data?.message || "Sale failed");
    }
    setLoading(false);
  };

  const printReceipt = () => {
    const printContent = receiptRef.current.innerHTML;
    const printFrame = document.createElement("iframe");
    printFrame.style.position = "absolute";
    printFrame.style.top = "-10000px";
    document.body.appendChild(printFrame);

    printFrame.contentDocument.write(`
      <html>
        <head>
          <title>Receipt</title>
          <style>
            @page { size: 57mm auto; margin: 0; }
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { 
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              font-size: 9px; 
              width: 57mm; 
              padding: 3mm; 
              background: #fff; 
              color: #000;
            }
            img { width: 16mm !important; height: 16mm !important; margin: 0 auto 2mm; display: block; }
            .text-center { text-align: center; }
            .font-bold { font-weight: 700; }
            .font-medium { font-weight: 500; }
            .font-semibold { font-weight: 600; }
            .text-lg { font-size: 11px; }
            .text-sm { font-size: 8px; }
            .text-xs { font-size: 7px; }
            .mb-3 { margin-bottom: 2mm; }
            .mb-2 { margin-bottom: 1.5mm; }
            .mb-1 { margin-bottom: 1mm; }
            .mt-2 { margin-top: 1.5mm; }
            .mt-1 { margin-top: 1mm; }
            .my-2 { margin-top: 1.5mm; margin-bottom: 1.5mm; }
            .p-6 { padding: 3mm; }
            .px-2 { padding-left: 1mm; padding-right: 1mm; }
            .py-1 { padding-top: 0.5mm; padding-bottom: 0.5mm; }
            .pt-1 { padding-top: 0.5mm; }
            .flex { display: flex; }
            .justify-between { justify-content: space-between; }
            .inline-block { display: inline-block; }
            .space-y-1 > * + * { margin-top: 0.5mm; }
            .border-t { border-top: 1px solid #d1d5db; }
            .border-dashed { border-style: dashed; }
            .border-gray-300 { border-color: #d1d5db; }
            .rounded { border-radius: 1mm; }
            .capitalize { text-transform: capitalize; }
            .bg-white { background: #fff; }
            .bg-blue-100 { background: #dbeafe; }
            .text-gray-800 { color: #1f2937; }
            .text-gray-500 { color: #6b7280; }
            .text-gray-400 { color: #9ca3af; }
            .text-blue-800 { color: #1e40af; }
            svg { display: none; }
            .w-16 { width: 16mm; }
            .h-16 { height: 16mm; }
            .mx-auto { margin-left: auto; margin-right: auto; }
          </style>
        </head>
        <body>${printContent}</body>
      </html>
    `);
    printFrame.contentDocument.close();
    printFrame.contentWindow.focus();
    printFrame.contentWindow.print();
    setTimeout(() => document.body.removeChild(printFrame), 1000);
  };

  const filteredItems = items.filter(
    (i) =>
      i.name.toLowerCase().includes(search.toLowerCase()) ||
      i.sku.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <ShoppingCart className="w-6 h-6" /> Point of Sale
        </h1>
        <div className="flex gap-4 text-sm">
          <div className="bg-green-100 px-4 py-2 rounded-lg">
            <span className="text-green-800 font-semibold">
              Today: ₦{parseFloat(stats.todaySales || 0).toLocaleString()}
            </span>
          </div>
          <div className="bg-blue-100 px-4 py-2 rounded-lg">
            <span className="text-blue-800 font-semibold">
              {stats.todayTransactions || 0} Sales
            </span>
          </div>
        </div>
      </div>

      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setActiveTab("sale")}
          className={`px-4 py-2 rounded ${activeTab === "sale" ? "bg-blue-600 text-white" : "bg-gray-200"}`}
        >
          New Sale
        </button>
        <button
          onClick={() => setActiveTab("history")}
          className={`px-4 py-2 rounded ${activeTab === "history" ? "bg-blue-600 text-white" : "bg-gray-200"}`}
        >
          Sales History
        </button>
      </div>

      {activeTab === "sale" ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Products */}
          <div className="lg:col-span-2 bg-white rounded-lg shadow p-4">
            <div className="relative mb-4">
              <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search items..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg"
              />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-h-[60vh] overflow-y-auto">
              {filteredItems.map((item) => (
                <div
                  key={item.id}
                  onClick={() => addToCart(item)}
                  className="border rounded-lg p-3 cursor-pointer hover:bg-blue-50 hover:border-blue-300 transition"
                >
                  <p className="font-medium truncate">{item.name}</p>
                  <p className="text-xs text-gray-500">{item.sku}</p>
                  <p className="text-blue-600 font-bold">
                    ₦
                    {parseFloat(
                      item.selling_price || item.unit_cost,
                    ).toLocaleString()}
                  </p>
                  <p className="text-xs text-gray-400">
                    Stock: {parseFloat(item.current_stock)}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Cart */}
          <div className="bg-white rounded-lg shadow p-4">
            <h2 className="font-bold mb-4 flex items-center gap-2">
              <Receipt className="w-5 h-5" /> Cart ({cart.length})
            </h2>
            <div className="space-y-2 max-h-[30vh] overflow-y-auto mb-4">
              {cart.map((c) => (
                <div
                  key={c.item_id}
                  className="flex items-center justify-between border-b pb-2"
                >
                  <div className="flex-1">
                    <p className="font-medium text-sm">{c.name}</p>
                    <p className="text-xs text-gray-500">
                      ₦{c.unit_price.toLocaleString()} × {c.quantity} = ₦
                      {(c.unit_price * c.quantity).toLocaleString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => updateQuantity(c.item_id, -1)}
                      className="p-1 bg-gray-100 rounded hover:bg-gray-200"
                    >
                      <Minus className="w-3 h-3" />
                    </button>
                    <input
                      type="number"
                      min="1"
                      max={c.max_stock}
                      value={c.quantity}
                      onChange={(e) => setQuantity(c.item_id, e.target.value)}
                      className="w-14 text-center text-sm border rounded py-1"
                    />
                    <button
                      onClick={() => updateQuantity(c.item_id, 1)}
                      className="p-1 bg-gray-100 rounded hover:bg-gray-200"
                    >
                      <Plus className="w-3 h-3" />
                    </button>
                    <button
                      onClick={() => removeFromCart(c.item_id)}
                      className="p-1 text-red-500 hover:bg-red-50 rounded"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
              {cart.length === 0 && (
                <p className="text-gray-400 text-center py-4">Cart is empty</p>
              )}
            </div>

            <div className="border-t pt-4 space-y-2">
              <input
                type="text"
                placeholder="Customer Name (optional)"
                value={customer.name}
                onChange={(e) =>
                  setCustomer({ ...customer, name: e.target.value })
                }
                className="w-full px-3 py-2 border rounded text-sm"
              />
              <input
                type="text"
                placeholder="Phone (optional)"
                value={customer.phone}
                onChange={(e) =>
                  setCustomer({ ...customer, phone: e.target.value })
                }
                className="w-full px-3 py-2 border rounded text-sm"
              />

              <div className="flex gap-2">
                <button
                  onClick={() => setPaymentMethod("cash")}
                  className={`flex-1 py-2 rounded flex items-center justify-center gap-1 text-sm ${paymentMethod === "cash" ? "bg-green-600 text-white" : "bg-gray-100"}`}
                >
                  <Banknote className="w-4 h-4" /> Cash
                </button>
                <button
                  onClick={() => setPaymentMethod("card")}
                  className={`flex-1 py-2 rounded flex items-center justify-center gap-1 text-sm ${paymentMethod === "card" ? "bg-green-600 text-white" : "bg-gray-100"}`}
                >
                  <CreditCard className="w-4 h-4" /> Card
                </button>
              </div>

              <div className="bg-gray-50 p-3 rounded text-sm">
                <div className="flex justify-between">
                  <span>Subtotal:</span>
                  <span>₦{subtotal.toLocaleString()}</span>
                </div>
                <div className="flex justify-between font-bold text-lg border-t mt-2 pt-2">
                  <span>Total:</span>
                  <span>₦{total.toLocaleString()}</span>
                </div>
              </div>

              <button
                onClick={completeSale}
                disabled={loading || !cart.length}
                className="w-full py-3 bg-blue-600 text-white rounded-lg font-bold disabled:bg-gray-300 hover:bg-blue-700"
              >
                {loading ? "Processing..." : "Complete Sale"}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
            <h2 className="font-bold">Recent Transactions</h2>
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gray-500" />
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="px-3 py-1.5 border rounded-lg text-sm"
                />
                <span className="text-gray-500">to</span>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="px-3 py-1.5 border rounded-lg text-sm"
                />
              </div>
            </div>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="p-2 text-left">Transaction #</th>
                <th className="p-2 text-left">Date</th>
                <th className="p-2 text-left">Customer</th>
                <th className="p-2 text-left">Payment</th>
                <th className="p-2 text-right">Total</th>
                <th className="p-2 text-center">Status</th>
                <th className="p-2 text-center">Receipt</th>
              </tr>
            </thead>
            <tbody>
              {transactions
                .filter((t) => {
                  const tDate = new Date(t.transaction_date)
                    .toISOString()
                    .split("T")[0];
                  if (dateFrom && tDate < dateFrom) return false;
                  if (dateTo && tDate > dateTo) return false;
                  return true;
                })
                .map((t) => (
                  <tr key={t.id} className="border-b hover:bg-gray-50">
                    <td className="p-2 font-mono">{t.transaction_number}</td>
                    <td className="p-2">
                      {new Date(t.transaction_date).toLocaleString()}
                    </td>
                    <td className="p-2">{t.customer_name || "-"}</td>
                    <td className="p-2 capitalize">{t.payment_method}</td>
                    <td className="p-2 text-right font-bold">
                      ₦{parseFloat(t.total_amount).toLocaleString()}
                    </td>
                    <td className="p-2 text-center">
                      <span
                        className={`px-2 py-1 rounded text-xs ${t.payment_status === "completed" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}
                      >
                        {t.payment_status}
                      </span>
                    </td>
                    <td className="p-2 text-center">
                      <button
                        onClick={() => {
                          const receiptItems = (
                            t.items || []
                          ).map((item) => ({
                            name:
                              item.InventoryItem?.name ||
                              item.item_name ||
                              "Item",
                            quantity: item.quantity,
                            unit_price: parseFloat(item.unit_price),
                          }));

                          setLastSale({
                            transaction_number: t.transaction_number,
                            date: new Date(t.transaction_date).toLocaleString(),
                            items: receiptItems,
                            subtotal: parseFloat(t.subtotal || t.total_amount),
                            total: parseFloat(t.total_amount),
                            customer: {
                              name: t.customer_name || "",
                              phone: t.customer_phone || "",
                            },
                            payment_method: t.payment_method,
                          });
                          setShowReceipt(true);
                        }}
                        className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                      >
                        <Printer className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Thermal Receipt Modal */}
      {showReceipt && lastSale && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 flex justify-between items-center">
              <h3 className="font-bold text-lg flex items-center gap-2">
                <Receipt className="w-5 h-5" /> Sales Receipt
              </h3>
              <button
                onClick={() => setShowReceipt(false)}
                className="p-1 hover:bg-white/20 rounded-full transition"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div ref={receiptRef} className="p-6 bg-white">
              {/* Header */}
              <div className="text-center mb-3">
                <img
                  src="/puffin_icon.png"
                  alt="Puffin"
                  className="w-16 h-16 mx-auto mb-2"
                />
                <h2 className="font-bold text-lg text-gray-800">
                  PUFFIN GROUP LTD
                </h2>
                <p className="text-xs text-gray-500">Sales Receipt</p>
                <div className="mt-2 inline-block bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-mono">
                  {lastSale.transaction_number}
                </div>
                <p className="text-xs text-gray-400 mt-1">{lastSale.date}</p>
              </div>

              <div className="border-t border-dashed border-gray-300 my-2"></div>

              {/* Items */}
              <div className="mb-2">
                <div className="space-y-1">
                  {lastSale.items.map((item, idx) => (
                    <div key={idx} className="text-xs">
                      <div className="flex justify-between font-medium">
                        <span>{item.name}</span>
                        <span>
                          ₦{(item.unit_price * item.quantity).toLocaleString()}
                        </span>
                      </div>
                      <div className="text-gray-500 text-xs">
                        ₦{item.unit_price.toLocaleString()} × {item.quantity}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="border-t border-dashed border-gray-300 my-2"></div>

              {/* Totals */}
              <div className="mb-2">
                <div className="flex justify-between text-sm mb-1">
                  <span>Subtotal:</span>
                  <span>₦{lastSale.subtotal.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-lg font-bold border-t border-gray-300 pt-1">
                  <span>TOTAL:</span>
                  <span>₦{lastSale.total.toLocaleString()}</span>
                </div>
              </div>

              <div className="border-t border-dashed border-gray-300 my-2"></div>

              {/* Payment Info */}
              <div className="text-xs mb-2">
                <div className="flex justify-between mb-1">
                  <span>Payment:</span>
                  <span className="font-bold capitalize">
                    {lastSale.payment_method}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Status:</span>
                  <span className="font-bold">Completed</span>
                </div>
              </div>

              {(lastSale.customer?.name || lastSale.customer?.phone) &&
                (lastSale.customer.name || lastSale.customer.phone) && (
                  <>
                    <div className="border-t border-dashed border-gray-300 my-2"></div>
                    <div className="text-xs mb-2">
                      <p className="font-semibold mb-1">Customer:</p>
                      {lastSale.customer?.name &&
                        lastSale.customer.name.trim() && (
                          <p>{lastSale.customer.name}</p>
                        )}
                      {lastSale.customer?.phone &&
                        lastSale.customer.phone.trim() && (
                          <p>{lastSale.customer.phone}</p>
                        )}
                    </div>
                  </>
                )}

              <div className="border-t border-dashed border-gray-300 my-2"></div>

              {/* Footer */}
              <div className="text-center text-xs">
                <p className="font-medium">Thank you!</p>
                <p className="text-gray-500 mt-1">Keep this receipt</p>
              </div>
            </div>

            <div className="p-4 bg-gray-50 border-t flex gap-3">
              <button
                onClick={printReceipt}
                className="flex-1 py-3 bg-blue-600 text-white rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-blue-700 transition shadow-lg shadow-blue-200"
              >
                <Printer className="w-5 h-5" /> Print Receipt
              </button>
              <button
                onClick={() => setShowReceipt(false)}
                className="flex-1 py-3 bg-gray-200 text-gray-700 rounded-xl font-semibold hover:bg-gray-300 transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default POS;
